import { z } from 'zod';
import type { Province } from '@/types';
import { validateSaId, type SaIdResult } from '@rosta/business-functions/sa-id';

export const roleSchema = z.enum(['candidate', 'employer', 'both']);

export const accountSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

export const profileTypeSchema = z.enum(['Individual', 'Organisation']);

export const availabilityEnum = [
  'Immediate',
  'TwoWeeks',
  'OneMonth',
  'Contract',
  'PerNegotiation',
] as const;

/** Human-readable labels for availability options (raw enum values are machine-facing). */
export const availabilityLabels: Record<string, string> = {
  Immediate: 'Immediately',
  TwoWeeks: 'Within two weeks',
  OneMonth: 'Within a month',
  Contract: 'Contract-based',
  PerNegotiation: 'To be negotiated',
};

export const workTypeEnum = ['Permanent', 'Contract', 'PartTime', 'Freelance', 'Any'] as const;

/** Human-readable labels for work-type options. */
export const workTypeLabels: Record<string, string> = {
  Permanent: 'Permanent',
  Contract: 'Contract',
  PartTime: 'Part-time',
  Freelance: 'Freelance',
  Any: 'Any',
};

export const basicProfileSchema = z.object({
  headline: z.string().min(5, 'Add a short headline (min 5 characters)'),
  availability: z.enum(availabilityEnum),
  workType: z.enum(workTypeEnum),
  province: z.string().min(1, 'Select a province'),
  city: z.string().min(2, 'Enter a city or suburb'),
});

// Optional by design: registration must complete even when the OFO database
// has no suitable match for the candidate's trade/skill. Marketplace visibility
// still requires >=1 skill later (Proposal Decision 8 — isMarketplaceVisible).
export const skillsSchema = z.array(z.string());

export type AccountInput = z.infer<typeof accountSchema>;
export type BasicProfileInput = z.infer<typeof basicProfileSchema>;

export const nationalIdTypeEnum = ['NationalId', 'Passport', 'TaxId'] as const;

export const nationalIdLabels: Record<string, string> = {
  NationalId: 'SA ID number',
  Passport: 'Passport',
  TaxId: 'Tax number (SARS)',
};

/** Validate SA ID via business function; returns valid=true only if checksum passes. */
function validateSaIdField(value: string, type: string): { valid: boolean; errors: string[] } {
  if (type !== 'NationalId') return { valid: true, errors: [] };
  const result: SaIdResult = validateSaId(value);
  return { valid: result.valid, errors: result.error ? [result.error] : [] };
}

export const nationalIdSchema = z
  .object({
    type: z.enum(nationalIdTypeEnum),
    number: z.string().min(1, 'ID number is required'),
    country: z.string().length(2).default('ZA'),
  })
  .refine(
    (data) => {
      if (data.type !== 'NationalId') return true;
      const result = validateSaIdField(data.number, data.type);
      return result.valid;
    },
    { message: 'Invalid SA ID number — checksum does not match', path: ['number'] },
  );

export type NationalIdInput = z.infer<typeof nationalIdSchema>;

export const TRADE_SKILLS = [
  'Electrical',
  'Plumbing',
  'Handyman',
  'Carpentry',
  'Painting',
  'Welding',
  'Tiling',
  'Roofing',
  'Paving',
  'Air Conditioning',
  'Solar Installations',
  'Pool Maintenance',
  'Gate & Fence',
  'Garden & Landscaping',
  'Appliance Repair',
];

export const PROVINCES: Province[] = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Western Cape',
];
